import { type MarketplaceItemId } from "@/services/marketplace/item/marketplaceItemTypes";
import type { AxiosResponse } from "axios";
import { ApiClient, TokenAdd } from "@/services/apiClient";
import { useCart } from "@/composables/marketplace/cart/cart";
import { useAnonymousCartPreferences } from "@/composables/marketplace/cart/useAnonymousCartPreferences.client";
import { handleAxiosError } from "@/utilities/exceptions";
import { type ShippingMethodId } from "@/services/marketplace/shippingMethod/shippingMethodTypes";
import { type PaymentMethodId } from "@/services/marketplace/paymentMethod/paymentMethodTypes";
import { type AddressId } from "@/services/addresses/addressTypes";
import { useUser } from "@/composables/user/user";
import { useVisitorFingerprint } from "@/composables/visitorFingerprint.client";
import { type UserId } from "@/services/user/userTypes";
import type { CartItemId } from "./cartTypes";
import { CartStatus, type ShoppingCart } from "./cartTypes";
import { ShoppingCart_Schema } from "./cartSchema";
import { assertCartExists, hasShippingAddress } from "./cartHelpers";

export const fetchFullCart = async (selectedSellerId?: UserId): Promise<ShoppingCart | undefined> => {
  const { isUserLoggedIn } = useUser();
  const fingerprint = isUserLoggedIn.value ? undefined : await useVisitorFingerprint().getFingerprint();

  // Cart ID is always fetched fresh — never cached — because it can change between calls.
  // For example, if the user deletes their cart on another device and creates a new one,
  // a stale local ID would cause us to request a cart that no longer exists.
  // Note: the API does not support fetching a cart directly by user ID or anonymous fingerprint,
  // so this two-step lookup (resolve ID first, then fetch cart) is unavoidable.
  const cartId = await fetchCartId(fingerprint);

  if (cartId === undefined) {
    return undefined;
  }

  const params = new URLSearchParams();
  if (fingerprint !== undefined) {
    params.set("anonymousIdentifier", fingerprint);
  }
  if (selectedSellerId !== undefined) {
    params.set("selectedSellerId", String(selectedSellerId));
  }
  const query = params.size > 0 ? "?" + params.toString() : "";
  const url = "/api-public/basket/basket/" + cartId + query;

  return ApiClient.getPublic<ShoppingCart>(url, {}, TokenAdd.IfAvailable)
    .then(({ data }) => enrichWithAnonymousPreferences(ShoppingCart_Schema.parse(data)))
    .catch(handleAxiosError({ 404: () => undefined }));
};

export const fetchCartId = async (anonymousIdentifier?: string): Promise<number | undefined> => {
  let url = "/api-public/basket/basket-status";
  let addUserToken = TokenAdd.Always;

  if (anonymousIdentifier !== undefined) {
    url += "?anonymousIdentifier=" + anonymousIdentifier;
    addUserToken = TokenAdd.Never;
  }

  return ApiClient.getPublic<{ id: number }>(url, {}, addUserToken)
    .then(({ data }) => data.id)
    .catch(handleAxiosError({ 404: () => undefined }));
};

export const fetchCartStatus = async (anonymousIdentifier?: string): Promise<CartStatus> => {
  let url = "/api-public/basket/basket-status";
  let addUserToken = TokenAdd.Always;

  if (anonymousIdentifier !== undefined) {
    url += "?anonymousIdentifier=" + anonymousIdentifier;
    addUserToken = TokenAdd.Never;
  }

  return ApiClient.getPublic(url, {}, addUserToken)
    .then(() => CartStatus.NotEmpty)
    .catch(handleAxiosError({ 404: () => CartStatus.Empty }));
};

export const addItemIntoCart = async (marketplaceItemId: MarketplaceItemId, quantity: number, anonymousIdentifier?: string): Promise<AxiosResponse> => {
  const url = "api-public/basket/basket-item";

  const payload = {
    marketPlaceItemId: marketplaceItemId, // mind different casing
    quantity,
    anonymousIdentifier,
  };

  const addUserToken = anonymousIdentifier !== undefined ? TokenAdd.Never : TokenAdd.Always;

  return ApiClient.postPublic(url, {}, payload, addUserToken);
};

export const updateCartItem = async (cartItemId: CartItemId, quantityDiff: number, anonymousIdentifier?: string): Promise<ShoppingCart | undefined> => {
  const url = "api-public/basket/basket-item/" + cartItemId;

  const payload = {
    id: cartItemId,
    quantity: quantityDiff,
    anonymousIdentifier,
  };

  const addUserToken = anonymousIdentifier !== undefined ? TokenAdd.Never : TokenAdd.Always;

  return ApiClient.putPublic(url, {}, payload, addUserToken).then(() => updateLocalCartFromApi());
};

export const deleteCartItem = async (cartItemId: CartItemId, anonymousIdentifier?: string): Promise<ShoppingCart | undefined> => {
  let url = "api-public/basket/basket-item/" + cartItemId;

  if (anonymousIdentifier !== undefined) {
    url += "?anonymousIdentifier=" + anonymousIdentifier;
  }

  const addUserToken = anonymousIdentifier !== undefined ? TokenAdd.Never : TokenAdd.Always;

  return ApiClient.deletePublic(url, {}, {}, addUserToken).then(() => updateLocalCartFromApi());
};

export const saveShippingMethod = async (methodId: ShippingMethodId, selectedSellerId?: UserId): Promise<ShoppingCart | undefined> => {
  const url = "api/delivery/basket-delivery";
  return ApiClient.post(url, {}, { delivery: methodId }).then(() => updateLocalCartFromApi(selectedSellerId));
};

export const attachShippingAddressToCart = async (cart: ShoppingCart, addressId: AddressId, selectedSellerId?: UserId): Promise<ShoppingCart | undefined> => {
  if (hasShippingAddress(cart)) {
    const url = "api/address/basket-address";
    return ApiClient.put(url, {}, { address: addressId }).then(() => updateLocalCartFromApi(selectedSellerId));
  } else {
    const url = "api/address/basket-address";
    return ApiClient.post(url, {}, { address: addressId }).then(() => updateLocalCartFromApi(selectedSellerId));
  }
};

export const savePaymentMethod = async (methodId: PaymentMethodId, selectedSellerId?: UserId): Promise<ShoppingCart | undefined> => {
  const url = "api/payment/basket-payment";
  return ApiClient.post(url, {}, { payment: methodId }).then(() => updateLocalCartFromApi(selectedSellerId));
};

export const mergeAnonymousCart = async (anonymousIdentifier: string): Promise<ShoppingCart | undefined> => {
  const url = "api/basket/basket-merge";
  return ApiClient.post(url, {}, { anonymousIdentifier }).then(() => updateLocalCartFromApi());
};

export const deleteCart = async (): Promise<AxiosResponse> => {
  const { cart } = useCart();
  assertCartExists(cart.value);
  const url = "api/basket/basket/" + cart.value.id;
  return ApiClient.delete(url).then((data) => {
    cart.value = undefined;
    return data;
  }); // Returns ID of deleted cart
};

export const addVoucherIntoCart = async (code: string, anonymousIdentifier?: string): Promise<AxiosResponse> => {
  const url = "api-public/basket/basket-voucher";
  const payload = {
    code,
    anonymousIdentifier,
  };
  const addUserToken = anonymousIdentifier !== undefined ? TokenAdd.Never : TokenAdd.Always;
  return ApiClient.postPublic(url, {}, payload, addUserToken);
};

export const removeVoucherFromCart = async (anonymousIdentifier?: string): Promise<AxiosResponse> => {
  const url = "api-public/basket/basket-voucher";
  const payload = {
    anonymousIdentifier,
  };
  return ApiClient.deletePublic(url, payload);
};

const updateLocalCartFromApi = async (selectedSellerId?: UserId): Promise<ShoppingCart | undefined> => {
  return fetchFullCart(selectedSellerId).then((updatedCart) => {
    const { cart } = useCart();
    cart.value = updatedCart;
    return updatedCart;
  });
};

export const enrichWithAnonymousPreferences = (cart: ShoppingCart): ShoppingCart => {
  const { isUserLoggedIn } = useUser();
  if (isUserLoggedIn.value) return cart;

  const { selectedShippingMethod, selectedPaymentMethod } = useAnonymousCartPreferences();
  return {
    ...cart,
    selectedShippingMethod: cart.selectedShippingMethod ?? selectedShippingMethod.value,
    selectedPaymentMethod: cart.selectedPaymentMethod ?? selectedPaymentMethod.value,
  };
};
